// @description see default export
/// <reference path="./ts-utility-types.d.ts" />

/**
 * Returns all keys in the object where key extends the KeyCondition type and the value in the object extends the ValueCondition type.
 * 
 * @example
 * type _T1 = FilterKeys<{ a: number; b: string; c: string[]; }, string>; // = 'b'
 * type _T2 = FilterKeys<{ a: number; b: string; c: string[]; }, Array<any>>; // = 'c'
 * type _T3 = FilterKeys<{ a: number; [mySymbol]: number; }, number, string>; // = 'a'
 */
export type FilterKeys<O, ValueCondition, KeyCondition = keyof any> = {
    [Key in keyof O]: Key extends KeyCondition ? O[Key] extends ValueCondition ? Key : never : never
}[keyof O];

export type FilterObject<O, Condition> = Pick<O, FilterKeys<O, Condition>>;

export type ArrayType<T extends ReadonlyArray<any>> = T extends ReadonlyArray<infer R> ? R : never;

export type PromiseResult<T extends Promise<any>> = T extends Promise<infer R> ? R : never;

export type Writeable<T extends {}> = {
    -readonly [P in keyof T]: T[P];
};

export type PartialRecord<K extends keyof any, T> = { [P in K]?: T; };

export type MakePartial<T, PartialKeys extends keyof T> = { [P in PartialKeys]?: T[P]; } & Omit<T, PartialKeys>;

/** Returns only the string keys of an object. Used to get the available keys of an enum object */
export const enumKeys = <T extends {}>(enumType: T) => Object.keys(enumType).filter(x => typeof x === 'string') as (keyof T)[];

/** Returns only the non-string values of an object. Used to get the available values of an enum object */
export const enumValues = <T extends {}>(enumType: T) => Object.values(enumType).filter(x => typeof x !== 'string') as (T[keyof T])[];

/** Object.entries with improved typings only */
export const objectEntries: <T>(obj: T) => [keyof T, T[keyof T]][] = Object.entries;

/** Object.fromEntries with improved typings only */
export const objectFromEntries: <K extends keyof any, T>(entries: Iterable<readonly [K, T]>) => Partial<Record<K, T>> = Object.fromEntries;

/** Object.fromEntries with improved typings only (returns non-partial record) */
export const objectFromEntriesNonPartial: <K extends keyof any, T>(entries: Iterable<readonly [K, T]>) => Record<K, T> = Object.fromEntries;

/** Object.keys with improved typings only */
export const objectKeys: <T>(obj: T) => (keyof T)[] = Object.keys;

/** Object.values with improved typings only */
export const objectValues: <T>(obj: T) => (T[keyof T])[] = Object.values;

/** class decorator to force static fields to be implemented */
export function StaticImplements<T>() {
    return (_constructor: T) => { /* nop */ };
}

/** 
 * Allows creating records with a fixed value type, but a dynamic, typed set of keys
 * 
 * @example
 * const MyMap = createRecord<{ value: number }>()({
 *   a: { value: 1 },
 *   b: { value: 2 },
 * });
 * // keyof typeof MyMap === 'a' | 'b';
 */
export function createRecord<V>() {
    return <K extends string>(rec: Record<K, V>) => rec;
}

/**
 * Type guard to filter out falsy values 
 */
export function isTruthy<T>(x: T | undefined | null | false | 0): x is T {
    return Boolean(x);
}
